import { FormEvent, useEffect, useState } from "react"
import { VisitorApi } from "./visitor-api"
import { CreateFollowUpDto, FollowUpDto, FollowUpStatus, Visitor } from "./visitor-dto"
import { formatDateTimeA } from "../../utils/dateFunction"
import DatePicker from "../../components/date-picker/DatePicker"

enum ContactType {
    Email,
    Phone,
    Text,
    "In Person"
}

const ContactIcons = [
    "fa-envelope",
    "fa-phone",
    "fa-message",
    "fa-user"
]

export const VisitorFollowUpEdit = ({ onClose, onSuccess, followUp, visitor }: { onClose: () => void, onSuccess: () => void, visitor: Visitor, followUp?: FollowUpDto }) => {

    const [requestData, setRequestData] = useState<CreateFollowUpDto>({} as CreateFollowUpDto)
    const [time, setTime] = useState('')
    const [date, setDate] = useState('')
    const [nextTime, setNextTime] = useState('')
    const [nextDate, setNextDate] = useState('')
    const [contactType, setContactType] = useState<ContactType>(0)

    const enumAllValues = Object.values(ContactType)
    const enumKeys = enumAllValues.splice(0, enumAllValues.length / 2)


    useEffect(() => {
        if (followUp) {
            const [d1, t1] = formatDateTimeA(followUp?.contacted_date)
            setDate(d1)
            setTime(t1)

            const [d2, t2] = formatDateTimeA(followUp?.next_proposed_contact_date)
            setNextDate(d2)
            setNextTime(t2)

            requestData.contact_mode = followUp.contact_mode
            requestData.notes = followUp.notes
        }
    }, [])
    const handleFollowUpData = (propName: keyof CreateFollowUpDto, value: any) => {
        setRequestData((r) => {
            return { ...r, [propName]: value }
        })
    }

    function saveFollowUp(data: CreateFollowUpDto) {
        VisitorApi.createFollowup(data).then(d => {
            onSuccess()
        }).catch(e => {
            alert(e)
        })
    }

    function updateFollowUp(followUpId: number, data: CreateFollowUpDto) {
        VisitorApi.updateFollowup(followUpId, data).then(d => {
            onSuccess()
        }).catch(e => {
            alert(e)
        })
    }

    function handleSubmit(event: FormEvent<HTMLFormElement>): void {

        event.preventDefault()

        const contacted_date = new Date(Date.parse(`${date} ${time}`)).toISOString()
        const next_proposed_contact_date = new Date(Date.parse(`${nextDate} ${nextTime}`)).toISOString()
        const data = {
            ...requestData,
            contacted_date,
            contact_mode: contactType,
            next_proposed_contact_date,
            visitor_id: visitor.id
        }
        if (followUp)
            updateFollowUp(followUp.id, data as any as CreateFollowUpDto)
        else
            saveFollowUp(data as any as CreateFollowUpDto)

    }

    return <div id="add-team-modal" className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] overflow-y-auto">
            <form onSubmit={handleSubmit}>
                <div className="border-b border-neutral-200 px-6 py-4 flex justify-between items-center">
                    <div className="flex items-center">
                        <div className="w-10 h-10 rounded-full bg-neutral-100 flex items-center justify-center mr-3">
                            <img src="https://api.dicebear.com/7.x/notionists/svg?scale=200&amp;seed=1001" alt="Sarah Johnson" className="h-8 w-8 rounded-full" />
                        </div>
                        <div>
                            <h3 className="text-lg text-neutral-800">Follow-up with Sarah Johnson</h3>
                            <p className="text-sm text-neutral-500">First visited on June 4, 2025</p>
                        </div>
                    </div>
                    <button type='button' className="text-neutral-500 hover:text-neutral-700" onClick={() => onClose()}>
                        <i className="fa-solid fa-xmark text-xl"></i>
                    </button>
                </div>

                <div className="p-6">
                    <div className="mb-6">
                        <div className="flex items-center justify-between">
                            <h4 className="text-sm text-black dark:text-white uppercase">Prospect Status</h4>
                            <div className="flex items-center">
                                <span className="text-sm text-neutral-500 mr-2">Mark as prospect</span>
                                <div className="relative inline-block w-10 mr-2 align-middle select-none">
                                    <input type="checkbox" id="prospect-toggle" checked={false} className="sr-only" />
                                    <label htmlFor="prospect-toggle" className="block overflow-hidden h-6 rounded-full bg-neutral-200 cursor-pointer">
                                        <span className="block h-6 w-6 rounded-full bg-neutral-800 transform translate-x-4"></span>
                                    </label>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="border border-neutral-200 rounded-lg p-5 mb-6">
                        <h4 className="text-sm text-black dark:text-white uppercase mb-4">Current Follow-up</h4>
                        <div className="space-y-4">
                            <div className="grid grid-cols-4 gap-4">
                                <div className="col-span-1">
                                    <label className="block text-sm text-black dark:text-white mb-1">Contact Method</label>
                                </div>
                                <div className="col-span-3">
                                    <div className="flex space-x-2">
                                        {enumKeys.map((k, i) => {
                                            return <button type='button' className={`px-3 py-2 ${i == contactType ? "bg-neutral-800 text-white" : "bg-white border border-neutral-300 text-neutral-700"} rounded-md flex items-center`} onClick={() => {
                                                setContactType(i)
                                            }}>
                                                <i className={`fa-solid ${ContactIcons[i]} mr-2`}></i>
                                                <span>{k}</span>
                                            </button>
                                        })}
                                    </div>
                                </div>
                            </div>


                            <div className="grid grid-cols-4 gap-4">
                                <div className="col-span-1">
                                    <label htmlFor="follow-up-notes" className="block text-sm text-black dark:text-white mb-1">Notes</label>
                                </div>
                                <div className="col-span-3">
                                    <textarea id="follow-up-notes" rows={2} className="w-full border border-neutral-300 rounded-md p-3 text-sm" placeholder="Enter details about the conversation..."
                                        value={requestData.notes} onChange={e => handleFollowUpData('notes', e.target.value)}></textarea>
                                </div>
                            </div>

                            <div className="grid grid-cols-4 gap-4">
                                <div className="col-span-1">
                                    <label className="block text-sm text-black dark:text-white mb-1">Contact Time</label>
                                </div>
                                <div className="col-span-3">
                                    <div className="flex space-x-3">
                                        <div className="w-1/2">
                                            <DatePicker
                                                value={date}
                                                onChange={(dateValue) => setDate(dateValue)}
                                                placeholder="Select date"
                                                className="w-full"
                                            />
                                        </div>
                                        <div className="w-1/2">
                                            <input type="time" value={time} className="w-full border border-neutral-300 rounded-md p-2 text-sm" onChange={e => {
                                                setTime(e.target.value)
                                            }} />
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="grid grid-cols-4 gap-4">
                                <div className="col-span-1">
                                    <label className="block text-sm text-black dark:text-white mb-1">Status</label>
                                </div>
                                <div className="col-span-3">
                                    <select className="w-full border border-neutral-300 rounded-md p-2 text-sm bg-white" onChange={e => {
                                        handleFollowUpData('status', Number(e.target.value))
                                    }}  >
                                        <option value={FollowUpStatus.COMPLETED}>Completed</option>
                                        <option value={FollowUpStatus.RESCHEDULED}>Re Scheduled</option>
                                        <option value={FollowUpStatus.NO_ANSWER}>No Answer</option>
                                        <option value={FollowUpStatus.CANCELLED}>Cancelled</option>
                                        <option value={FollowUpStatus.LEAD}>Lead</option>
                                        <option value={FollowUpStatus.CONVERTED}>Converted</option>
                                    </select>
                                </div>
                            </div>
                            <div className="grid grid-cols-4 gap-4">
                                <div className="col-span-1">
                                    <label className="block text-sm text-black dark:text-white mb-1">Next Call Scheduled For</label>
                                </div>
                                <div className="col-span-3">
                                    <div className="flex space-x-3">
                                        <div className="w-1/2">
                                            <DatePicker
                                                value={nextDate}
                                                onChange={(dateValue) => setNextDate(dateValue)}
                                                placeholder="Select date"
                                                className="w-full"
                                            />
                                        </div>
                                        <div className="w-1/2">
                                            <input type="time" value={nextTime} className="w-full border border-neutral-300 rounded-md p-2 text-sm" onChange={(e => setNextTime(e.target.value))} />
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </div>

                    </div>


                </div>
                <div className="border-t border-neutral-200 px-6 py-4 flex justify-between">
                    <div>
                        <button type='button' className="px-4 py-2 border border-neutral-300 rounded-md text-neutral-700 hover:bg-neutral-50" onClick={() => {
                            onClose()
                        }}>
                            Cancel
                        </button>
                    </div>
                    <div className="flex space-x-3">
                        <button type='submit' className="px-4 py-2 bg-neutral-800 text-white rounded-md hover:bg-neutral-700" >
                            Save Follow-up
                        </button>
                    </div>
                </div>
            </form>
        </div>

    </div>


}